/**
 * MK.4 — Per-skill "Train a LoRA from this skill" button.
 *
 * Sits in the SkillLibraryWindow detail panel. Clicking it kicks the
 * full Tinker training pipeline scoped to a single Skill via the
 * `single-skill:<name>` dataset scope (see convex/tinkerDataset.ts).
 *
 * Defaults:
 *   - baseModel: Qwen/Qwen3-8B (the same default as TinkerTab).
 *   - loraRank: 8 (small, fast, cheap — appropriate for a one-skill
 *     demo training pass).
 *   - epochs: 1
 *   - learningRate: 0.0002
 *   - costCapUsd: 5
 *
 * Status banner mirrors the TinkerTab "requesting → kicking → queued
 * → running → complete | error" lifecycle.
 */
import { useEffect, useRef, useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import { useAppDispatch } from '../../store';
import {
  addSession,
  addFork,
  brandId,
  PromptSessionStatus,
  type PromptSession,
  type PromptSessionId,
  type TimelineFork,
  type TimelineForkId,
} from '@repancy/core';
import { useWindowContext } from '../desktop/WindowContext';
import { useNipeControl } from '../nipe/NipeControlContext';
import { setLabHandoff } from '../specialization-lab/labHandoff';

interface TrainLoraButtonProps {
  skillId: string;
  skillName: string;
}

type LocalStatus =
  | 'idle'
  | 'requesting'
  | 'kicking'
  | 'queued'
  | 'running'
  | 'complete'
  | 'error';

export function TrainLoraButton({
  skillId,
  skillName,
}: TrainLoraButtonProps): React.ReactElement {
  const requestJob = useMutation(api.tinkerTrainingJobs.request);
  const kickWorker = useAction(api.tinkerTrainingJobs.kickWorker);
  const currentJob = useQuery(
    api.tinkerTrainingJobs.currentForUser,
    {},
  );
  const [localStatus, setLocalStatus] = useState<LocalStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [primerOpen, setPrimerOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { openWindow } = useWindowContext();
  const nipe = useNipeControl();

  // L.1 — once the trainer creates a tinkerSpikeRuns row + registers
  // a LoRA URL via `lorasByPortal:registerLora`, we look it up here so
  // we can construct a lora-toggle fork the user actually sees.
  const sourceRunRowId = currentJob?.spikeRunRowId ?? null;
  const registeredLora = useQuery(
    api.lorasByPortal.findBySourceRun,
    sourceRunRowId !== null
      ? { sourceRunRowId }
      : 'skip',
  ) as
    | { tinkerLoraUrl: string; baseModel: string; loraRank: number }
    | null
    | undefined;

  // Show the live job status if the job we kicked is still in flight.
  const remoteStatus = currentJob?.status as
    | 'queued'
    | 'running'
    | 'complete'
    | 'failed'
    | undefined;
  const effective =
    localStatus === 'idle' || localStatus === 'complete' || localStatus === 'error'
      ? localStatus
      : (remoteStatus === 'running'
          ? 'running'
          : remoteStatus === 'complete'
            ? 'complete'
            : localStatus);

  const isBusy =
    effective === 'requesting' ||
    effective === 'kicking' ||
    effective === 'queued' ||
    effective === 'running';

  // L.1 — when the trainer flips to `complete`, create a sandbox
  // PromptSession + a `lora-toggle` fork in Redux, then open the
  // Specialization Lab (L.10). The lora-toggle fork is also
  // visible in PromptLab's embedded Timeline panel via the Redux
  // forks slice. Guarded by a per-spikeRun ref so we only fire
  // once per training pass even with React rerenders.
  const handledSpikeRunRef = useRef<string | null>(null);
  useEffect(() => {
    if (effective !== 'complete') return;
    if (!sourceRunRowId) return;
    if (handledSpikeRunRef.current === sourceRunRowId) return;

    const parentId = brandId<'PromptSession'>(
      `sandbox-base-${sourceRunRowId}`,
    ) as PromptSessionId;
    const forkedId = brandId<'PromptSession'>(
      `sandbox-lora-${sourceRunRowId}`,
    ) as PromptSessionId;
    const nowIso = new Date().toISOString();

    const baseSession: PromptSession = {
      id: parentId,
      systemPrompt: '',
      userPrompt: '',
      skillPrompts: [],
      modelId: registeredLora?.baseModel ?? 'meta-llama/Llama-3.2-1B',
      documentContextIds: [],
      status: PromptSessionStatus.Idle,
      createdAt: nowIso,
      userId: 'sandbox',
    };
    const loraSession: PromptSession = {
      ...baseSession,
      id: forkedId,
    };
    dispatch(addSession(baseSession));
    dispatch(addSession(loraSession));

    const forkId = brandId<'TimelineFork'>(
      `lora-toggle-${sourceRunRowId}`,
    ) as TimelineForkId;
    const fork: TimelineFork = {
      id: forkId,
      forkStepIndex: 0,
      parentSessionId: parentId,
      forkedSessionId: forkedId,
      label: `Specialized on: ${skillName}`,
      deltas: [
        {
          kind: 'lora-toggle',
          field: 'lora',
          oldValue: '',
          newValue: registeredLora?.tinkerLoraUrl ?? '',
          timestamp: Date.now(),
          metadata: {
            loraUrl: registeredLora?.tinkerLoraUrl,
            baseModel: registeredLora?.baseModel,
            loraRank: registeredLora?.loraRank,
            sourceSkillNames: [skillName],
            sourceRunId: sourceRunRowId,
          },
        },
      ],
    };
    dispatch(addFork(fork));
    handledSpikeRunRef.current = sourceRunRowId;
    // L.10 — auto-open the Specialization Lab. The Lab pulls the same
    // tinkerSpikeRuns row + lorasByPortal registry that we just wrote
    // the fork from, so the user lands directly on the compare surface.
    // F18.G.3 — also seed the handoff so the Lab's header skill picker
    // lands on the just-trained skill instead of (alphabetically)
    // whatever ships first in the marketplace.
    setLabHandoff({ skillName, source: 'TrainLoraButton.completion' });
    openWindow('/specialization-lab');
  }, [
    effective,
    sourceRunRowId,
    registeredLora,
    skillName,
    dispatch,
    openWindow,
  ]);

  async function handleClick() {
    setError(null);
    setLocalStatus('requesting');
    try {
      const jobId = await requestJob({
        baseModel: 'Qwen/Qwen3-8B',
        loraRank: 8,
        epochs: 1,
        learningRate: 0.0002,
        costCapUsd: 5,
        datasetScopeJson: JSON.stringify([`single-skill:${skillName}`]),
      });
      setLocalStatus('kicking');
      const result = await kickWorker({
        jobId,
      });
      if (!result.ok) {
        setLocalStatus('error');
        setError(result.reason ?? 'Worker unreachable.');
        return;
      }
      setLocalStatus('queued');
    } catch (err) {
      setLocalStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const labelByStatus: Record<LocalStatus, string> = {
    idle: 'Specialize the model on this skill',
    requesting: 'Snapshotting settings…',
    kicking: 'Kicking worker…',
    queued: 'Queued — waiting for worker',
    running: 'Specializing…',
    complete: '✓ Specialized — re-specialize',
    error: '✗ Error — retry',
  };

  return (
    <div className="flex flex-col gap-1 relative">
      <div className="flex items-center gap-1">
        <button
          type="button"
          data-testid={testIds.skills.libraryRowTrainButton(skillId)}
          onClick={() => void handleClick()}
          disabled={isBusy}
          className="text-[11px] px-3 py-1 cursor-pointer font-bold disabled:opacity-50"
          style={{
            background: 'linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4)',
            color: '#000',
            border: '1px solid #003c74',
            borderRadius: 3,
          }}
        >
          {labelByStatus[effective] ?? labelByStatus.idle}
        </button>
        <button
          type="button"
          aria-label="What's a specialization?"
          title="What's a specialization?"
          data-testid={testIds.skills.loraPrimerToggle(skillId)}
          onClick={() => setPrimerOpen((v) => !v)}
          className="text-[11px] w-5 h-5 flex items-center justify-center cursor-pointer font-bold leading-none"
          style={{
            background: '#ece9d8',
            color: '#000',
            border: '1px solid #808080',
            borderRadius: '50%',
          }}
        >
          ?
        </button>
      </div>
      {primerOpen && (
        <div
          data-testid={testIds.skills.loraPrimerPopover(skillId)}
          className="absolute z-20 mt-7 max-w-[320px] bg-[#ece9d8] border border-[#7f9db9] p-2 text-[10px] text-black shadow-[2px_2px_0_0_#404040]"
        >
          <div className="font-bold mb-1">What's a specialization?</div>
          <p className="mb-1">
            A small adjustment to the base model that focuses it on certain
            patterns and quiets others, built from the Skills you pick.
          </p>
          <p className="mb-1">
            <span className="font-bold">Additive and subtractive</span>: it
            amplifies words and structures common across your Skills, and
            de-emphasizes the moves that show up less. Not new legal knowledge
            — just a style overlay you can take off any time.
          </p>
          <p className="mb-1">
            (Called a <span className="italic">LoRA</span> in the operator UI
            — same thing, technical name.)
          </p>
          <button
            type="button"
            data-testid={testIds.skills.loraPrimerOpenNipe(skillId)}
            onClick={() => {
              setPrimerOpen(false);
              nipe.open();
            }}
            className="text-[10px] text-[#0000ee] underline cursor-pointer bg-transparent border-0 p-0"
          >
            Ask NIPE for the full explanation →
          </button>
        </div>
      )}
      {(effective === 'queued' || effective === 'running') && (
        <span
          data-testid={testIds.skills.libraryRowTrainStatus(skillId)}
          className="text-[10px] text-black"
        >
          Specialization in progress — the Lab opens automatically when it finishes.
        </span>
      )}
      {effective === 'error' && error !== null && (
        <span
          data-testid={testIds.skills.libraryRowTrainStatus(skillId)}
          className="text-[10px] text-[#7a0a0a] font-bold"
        >
          {error}
        </span>
      )}
      {effective === 'complete' && (
        <span
          data-testid={testIds.skills.libraryRowTrainStatus(skillId)}
          className="text-[10px] text-[#1a5c1a] font-bold"
        >
          ✓ The model is specialized on this skill. Open the Specialization Lab
          (✨ badge) to see what changed.
        </span>
      )}
    </div>
  );
}
